import { Star, ExternalLink } from "lucide-react";
import { useReveal } from "@/hooks/use-reveal";
import { Button } from "@/components/ui/button";
import { BUSINESS } from "@/lib/constants";
import { useContent } from "@/lib/content";

export default function ReviewsSection() {
  const r = useReveal();
  const content = useContent();
  const s = content?.settings || {};
  const rating = s.rating_value ?? 4.8;
  const reviewCount = s.rating_count ?? 61;
  const writeLink = s.write_review_link || BUSINESS.writeReviewLink;
  const mapsLink = s.maps_link || BUSINESS.mapsLink;
  const testimonials = (content?.testimonials && content.testimonials.length > 0)
    ? content.testimonials
    : [
        { title: "Editable Placeholder", quote: "Reviews are not fabricated. Add genuine ones from the admin.", name: "— Placeholder" },
        { title: "Editable Placeholder", quote: "Ask a happy customer to share their FASTag experience.", name: "— Placeholder" },
        { title: "Editable Placeholder", quote: "Real customer reviews will appear here after verification.", name: "— Placeholder" },
        { title: "Editable Placeholder", quote: "Post genuine reviews to strengthen local SEO.", name: "— Placeholder" },
      ];
  return (
    <section className="py-20 sm:py-24 bg-[#0B1220] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #0057B8 0, transparent 40%), radial-gradient(circle at 80% 60%, #0E7490 0, transparent 45%)" }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div ref={r.ref} className={`${r.className} grid lg:grid-cols-12 gap-10 items-center`}>
          <div className="lg:col-span-5">
            <span className="text-sm font-semibold uppercase tracking-widest text-[#F59E0B]">What Customers Say</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Trusted by motorists <span className="text-[#F59E0B]">across Howrah</span>
            </h2>
            <p className="mt-4 text-white/70 leading-relaxed">
              We invite genuine feedback from our customers. Please leave a review on Google to help others discover us.
            </p>

            <div className="mt-6 inline-flex items-center gap-3 bg-white/10 border border-white/15 rounded-xl px-4 py-3">
              <div className="flex items-center gap-0.5 text-[#F59E0B]">
                {[0,1,2,3,4].map(i => <Star key={i} className="w-4 h-4 fill-[#F59E0B]" />)}
              </div>
              <div className="text-sm">
                <div className="font-semibold">{rating} / 5 <span className="text-white/70 font-normal">· {reviewCount} Google reviews</span></div>
                <div className="text-white/60">Verified via our Google Business listing.</div>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full btn-brand">
                <a href={writeLink} target="_blank" rel="noreferrer" data-testid="reviews-write-cta">
                  Write a Google Review <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </Button>
              <Button asChild variant="outline" className="border-white/40 text-white bg-white/10 hover:bg-white hover:text-[#0B1220] rounded-full btn-brand">
                <a href={mapsLink} target="_blank" rel="noreferrer" data-testid="reviews-google-cta">
                  Read All Reviews
                </a>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {testimonials.map((c, i) => (
              <div key={i} className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-5">
                <div className="flex gap-0.5 text-[#F59E0B] mb-2">
                  {Array.from({ length: c.rating || 5 }).map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-[#F59E0B]" />)}
                </div>
                <p className="text-sm text-white/85 leading-relaxed">"{c.quote || c.body}"</p>
                <div className="mt-3 flex items-center gap-2">
                  {c.photo && <img src={c.photo} alt="" className="w-7 h-7 rounded-full object-cover" />}
                  <div>
                    <div className="font-display font-semibold text-white text-sm">{c.name}</div>
                    {c.title && <div className="text-[10px] text-white/60">{c.title}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
